// src/services/authService.js - v3 - Refined Error Handling, UIController Integration
import firebase, { auth, db } from '../firebaseConfig.js';
import { showGeneralMessage } from '../ui/uiUtils.js'; // Import for general message display
import { clearTokens } from './tokenService.js'; // Import token clearing utility
import { clearSpecialAccess, hasSpecialAccess, getSpecialAccessUserId, getSpecialAccessDisplayName } from './specialAccessService.js'; // Import special access utilities
import sceneManager from '../utils/SceneManager.js';


/** Check if current user is logged in and email verified. */
export function isUserVerified() {
  const user = auth.currentUser;
  return !!user && user.emailVerified;
}

/** Validate password strength. */
export function validatePassword(password) {
  if (!password || password.length < 8) return { isValid: false, message: "Password must be at least 8 characters" };
  if (!/[A-Z]/.test(password)) return { isValid: false, message: "Password needs an uppercase letter" };
  if (!/[a-z]/.test(password)) return { isValid: false, message: "Password needs a lowercase letter" };
  if (!/[0-9]/.test(password)) return { isValid: false, message: "Password needs a number" };
  if (!/[^A-Za-z0-9]/.test(password)) return { isValid: false, message: "Password needs a special character" };
  return { isValid: true, message: "Password is strong" };
}

/** Sign in with email and password. */
// --- Replace your existing signInWithEmail function with this ---
// --- REPLACE THE ENTIRE signInWithEmail FUNCTION ---
export async function signInWithEmail(email, password) {
  try {
      // Rate limiting check
      const failedAttempts = parseInt(localStorage.getItem('failedLoginAttempts') || '0');
      const lastAttemptTime = parseInt(localStorage.getItem('lastLoginAttemptTime') || '0');
      const now = Date.now();
      const lockoutDuration = 300000; // 5 minutes

      if (failedAttempts >= 5 && (now - lastAttemptTime) < lockoutDuration) {
          const timeLeft = Math.ceil((lockoutDuration - (now - lastAttemptTime)) / 1000 / 60);
          throw new Error(`Too many login attempts. Please try again in ${timeLeft} minute(s).`);
      }
      // Reset attempts if lockout duration passed
      if ((now - lastAttemptTime) > lockoutDuration) {
          localStorage.setItem('failedLoginAttempts', '0');
      }
      localStorage.setItem('lastLoginAttemptTime', now.toString());

      console.log("Attempting Firebase sign in...");
      const userCredential = await auth.signInWithEmailAndPassword(email, password);
      localStorage.setItem('failedLoginAttempts', '0'); // Reset attempts on success

      if (!userCredential.user) {
           throw new Error("Authentication succeeded but user object is missing.");
      }

      console.log(`Firebase sign in successful for ${email}. Checking verification...`);

      if (!userCredential.user.emailVerified) {
          console.warn(`User ${email} not verified. Signing out.`);
          await auth.signOut(); // Sign out immediately if not verified

          // --- Use window.UIController or fallback ---
          if (window.UIController && typeof window.UIController.showOverlayMessage === 'function') {
            window.UIController.showOverlayMessage('auth-overlay', "Email not verified. Please check your inbox or Sign Up.", true);
          } else {
            showGeneralMessage("Email not verified. Please check your inbox or Sign Up.", 3000, true); // Use imported function
          }
          // --- End Change ---

          return false; // Indicate verification failure
      }

      console.log(`Sign in AND verification successful for ${userCredential.user.email}`);

      // --- Claim Anonymous Scores ---
      try {
           let displayName = userCredential.user.email; // Default display name
           const profileDoc = await db.collection("userProfiles").doc(userCredential.user.uid).get();
           if (profileDoc.exists && profileDoc.data().displayName) {
               displayName = profileDoc.data().displayName;
           }
           console.log(`Attempting to claim scores for user ${userCredential.user.uid} with display name ${displayName}`);
           await claimAnonymousScores(userCredential.user.uid, displayName);
      } catch (err) {
           console.error("Error claiming scores during sign in:", err);
           // Non-critical, continue with login
      }
      // --- End Claim Scores ---

      console.log("Sign-in success: Hiding overlay and delaying slightly before triggering auth state update.");
      // --- Use window.UIController ---
      if (window.UIController && typeof window.UIController.hideOverlay === 'function') {
           window.UIController.hideOverlay('auth-overlay');
       }
       // --- End Use ---
      await new Promise(resolve => setTimeout(resolve, 250)); // Wait 250ms

      console.log("Dispatching 'auth-change' event after successful sign-in.");
      window.dispatchEvent(new CustomEvent('auth-change'));

      return userCredential; // Return credential on success

  } catch (err) {
      console.error("Sign-in error:", err);
      clearTokens(); // Clear any potentially invalid tokens

      // Increment failed attempts counter
      const currentFailedAttempts = parseInt(localStorage.getItem('failedLoginAttempts') || '0');
      localStorage.setItem('failedLoginAttempts', (currentFailedAttempts + 1).toString());

      // Determine user-friendly error message
      let message = `Sign-in failed. Please try again.`; // Default message
       if (err.code === "auth/user-not-found" || err.code === "auth/invalid-credential" || err.code === "auth/wrong-password") {
           message = "Incorrect email or password.";
       } else if (err.code === "auth/invalid-email") {
           message = "Invalid email format.";
       } else if (err.message && err.message.includes("Too many login attempts")) {
           message = err.message; // Use the specific lockout message
       } else if (err.code === 'auth/network-request-failed') {
           message = "Network error. Please check your connection.";
       }

       // --- Use window.UIController or fallback ---
       if (window.UIController && typeof window.UIController.showOverlayMessage === 'function') {
           window.UIController.showOverlayMessage('auth-overlay', message, true);
       } else {
           showGeneralMessage(message, 3000, true); // Use imported function
       }
       // --- End Change ---
      return false; // Indicate failure
  }
}
// --- END REPLACE signInWithEmail ---
// --- REPLACE THE ENTIRE signUpWithEmail FUNCTION ---
export async function signUpWithEmail(email, password, username, marketingConsent = false, dataSharingConsent = false) {
  try {
    // Validate email format
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return { success: false, message: "Please enter a valid email address" };
    }

    // Validate password strength
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      return { success: false, message: passwordValidation.message };
    }

     // Validate username
     if (!username || username.length < 3) {
          return { success: false, message: "Display Name must be at least 3 characters" };
     }

    const userCredential = await auth.createUserWithEmailAndPassword(email, password);
    const user = userCredential.user;

    // Send verification email IMMEDIATELY after creation
    try {
       await user.sendEmailVerification();
       console.log(`Verification email sent to ${email}`);
    } catch (verificationError) {
       console.error("Failed to send verification email:", verificationError);
    }

    // Set display name
    try {
       await user.updateProfile({ displayName: username });
    } catch (profileUpdateError) {
        console.error("Failed to update Firebase Auth profile display name:", profileUpdateError);
    }

    // Create user profile in Firestore
    await db.collection("userProfiles").doc(user.uid).set({
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      displayName: username,
      email: email,
      marketingConsent: !!marketingConsent,
      dataSharingConsent: !!dataSharingConsent,
      role: "user",
      emailVerified: false
    });

    console.log(`User ${email} signed up successfully. Profile created.`);
    // Let the calling UI handle showing the message
    return { success: true, message: "Account created! Please check your email to verify your account." };
  } catch (err) {
    console.error("Sign-up error:", err);

    let message = `Sign-up failed: ${err.message}`;
    if (err.code === "auth/email-already-in-use") {
      message = "This email is already registered. Please Sign In instead.";
    } else if (err.code === "auth/invalid-email") {
      message = "Please enter a valid email address.";
    } else if (err.code === "auth/weak-password") {
      message = "Password is too weak.";
    }
    // Let the calling UI handle showing the message
    return { success: false, message };
  }
}
// --- END REPLACE signUpWithEmail ---
/** Sign up with email and password. */


/** Sign out the current user. */
// --- REPLACE THE ENTIRE signOutUser FUNCTION ---
export async function signOutUser() {
  try {
    // Clear any local data related to the user
    localStorage.removeItem('userPreferences');
    localStorage.removeItem('failedLoginAttempts'); // Also clear login attempts
    localStorage.removeItem('lastLoginAttemptTime');
    clearTokens();
    clearSpecialAccess(); // Clear any Guest+ session data on standard logout

    // Sign out from Firebase Auth
    await auth.signOut();
    console.log("User signed out successfully.");
    // No message needed here, auth listener will handle UI/scene changes
    return true;
  } catch (error) {
    console.error("Error signing out:", error);
    showGeneralMessage("Sign out failed. Please try again.", 3000, true); // Use general message
    return false;
  }
}
// --- END REPLACE signOutUser ---

/** 
 * Claims anonymous scores for a verified user.
 * Called after sign-in and email verification to associate guest scores with the user account.
 */
// --- REPLACE THE ENTIRE claimAnonymousScores FUNCTION ---
export async function claimAnonymousScores(userId, displayName) {
  if (!userId) {
    console.error("Cannot claim scores: No user ID provided");
    return false; // Indicate failure
  }

  try {
    const tempName = localStorage.getItem("tempSimpsonsName");
    if (!tempName) {
      console.log("No temporary name found to claim scores for");
      return true; // Nothing to claim, considered success
    }

    const localScores = JSON.parse(localStorage.getItem("localScores") || "[]");
    if (localScores.length === 0) {
      console.log("No local scores found to claim");
      return true; // Nothing to claim, considered success
    }

    console.log(`Attempting to claim ${localScores.length} scores for ${displayName}`);

    const batch = db.batch();
    localScores.forEach(score => {
      const newScoreRef = db.collection("scores").doc();
      batch.set(newScoreRef, {
        userUID: userId,
        player: displayName, // Use the verified user's display name
        score: score.score,
        game: score.game,
        verified: true, // Mark as verified now
        createdAt: firebase.firestore.FieldValue.serverTimestamp(), // Use server time
        claimedFrom: "localScore",
        formerPlayer: tempName // Keep track of the original guest name
      });
    });

    await batch.commit();
    localStorage.removeItem("localScores"); // Clear local scores ONLY after successful commit
    console.log(`Successfully claimed ${localScores.length} scores for ${displayName}`);

    showGeneralMessage(`Claimed ${localScores.length} previous score(s)!`, 3000, false); // Use general message
    return true; // Indicate success
  } catch (error) {
    console.error("Error claiming anonymous scores:", error);
    showGeneralMessage("Failed to claim previous scores.", 3000, true); // Use general message
    return false; // Indicate failure
  }
}
// --- END REPLACE claimAnonymousScores ---
/**
 * Saves a game score to Firestore for authenticated users or locally for guests.
 * @param {Object} scoreData - The score data object containing score and metadata
 * @param {boolean} skipOverlay - Whether to skip showing the success/error overlay
 * @returns {Promise<boolean>} Success status
 */
// --- REPLACE THE ENTIRE saveScoreToFirestore FUNCTION ---
export async function saveScoreToFirestore(scoreData, skipOverlay = false) {
  // Destructure with defaults and handle potential missing fields gracefully
  const { score, gameType, player, userUID, teamName, timestamp, ...additionalData } = scoreData || {};

  try {
      if (typeof score !== 'number' || isNaN(score)) throw new Error("Invalid score provided");
      if (!gameType || typeof gameType !== 'string') throw new Error("Missing or invalid gameType");

      const user = auth.currentUser;
      const isVerified = user && user.emailVerified;
      const specialAccess = hasSpecialAccess();

      let finalPlayerName = player || "Player"; // Default name
      let finalUserId = userUID || user?.uid;
      let isGuestScore = !isVerified; // Default to guest unless verified auth user

      // --- Determine Identity and Name ---
      if (isVerified) {
          isGuestScore = false;
          finalUserId = user.uid; // Ensure correct ID for verified user
          try {
              const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
              if (profileDoc.exists && profileDoc.data().displayName) {
                  finalPlayerName = profileDoc.data().displayName;
              } else {
                  finalPlayerName = user.displayName || user.email || "Player"; // Fallback chain
              }
          } catch (e) {
              console.warn("Could not fetch profile during score save:", e);
              finalPlayerName = player || user?.displayName || user?.email || "Player"; // Use provided player name if fetch fails
          }
      } else if (specialAccess && !user) {
          isGuestScore = true;
          finalUserId = getSpecialAccessUserId();
          finalPlayerName = getSpecialAccessDisplayName(); // Use Guest+ name
      } else { // Regular Guest or Unverified Auth User
          isGuestScore = true;
          finalPlayerName = localStorage.getItem("tempSimpsonsName") || `Guest-${Math.floor(Math.random() * 10000)}`;
          finalUserId = `guest_${finalPlayerName}`; // Use guest ID format
          if (!localStorage.getItem("tempSimpsonsName")) {
              localStorage.setItem("tempSimpsonsName", finalPlayerName);
          }
      }
      // --- End Identity Determination ---

      // --- Saving Logic ---
      if (!isGuestScore) { // Save to Firestore for Verified Users
          const firestoreScoreData = {
              score: score,
              game: gameType,
              player: finalPlayerName, // Use the determined finalPlayerName
              userUID: finalUserId,
              verified: true,
              createdAt: timestamp || firebase.firestore.FieldValue.serverTimestamp(),
              teamName: teamName || null, // Include teamName if provided
              ...additionalData // Include any extra data passed in
          };
          const scoreRef = await db.collection("scores").add(firestoreScoreData);
          console.log(`Score saved to Firestore (ID: ${scoreRef.id}) for ${finalPlayerName}`);
          if (!skipOverlay) showGeneralMessage("Score saved!", 2000, false);

      } else { // Save Locally for Guests, Guest+, Unverified
          console.log(`Saving score locally for ${finalPlayerName} (UserID: ${finalUserId})`);
          const localScore = {
             score: score,
             game: gameType,
             player: finalPlayerName, // Use the determined finalPlayerName
             userId: finalUserId, // Store the appropriate guest/guest+ ID
             timestamp: Date.now(),
             expiresAt: Date.now() + (30 * 24 * 60 * 60 * 1000), // 30-day expiry
             teamName: teamName || null,
             ...additionalData
          };
          const existingScores = JSON.parse(localStorage.getItem("localScores") || "[]");
          existingScores.push(localScore);
          const sortedScores = existingScores.sort((a, b) => b.score - a.score).slice(0, 20);
          localStorage.setItem("localScores", JSON.stringify(sortedScores));

          if (!skipOverlay) {
              const message = (!isVerified && user)
                  ? "Score saved locally! Verify email to save permanently."
                  : "Score saved locally! Sign in to save permanently.";
              showGeneralMessage(message, 3000, false);
          }
      }
      return true; // Indicate success

  } catch (error) {
      console.error("Error saving score:", error);
      if (!skipOverlay) showGeneralMessage(`Failed to save score: ${error.message}`, 3000, true);
      return false; // Indicate failure
  }
}
// --- END REPLACE saveScoreToFirestore ---


// Export the functions
export default {
  isUserVerified,
  validatePassword,
  signInWithEmail,
  signUpWithEmail,
  signOutUser,
  claimAnonymousScores,
  saveScoreToFirestore
};